/**
 * The hymnal reference app — a SEPARATE build from studio/client, served from the
 * same origin under /hymnal/ (plan D9). Routes are absolute (`/hymnal/...`) because
 * this app owns that prefix and the prerenderer writes files at those paths.
 *
 * Pages are STATICALLY imported, never React.lazy: the prerenderer renders through
 * renderToString, which is synchronous and cannot await a lazy() chunk.
 *
 * The header, footer and nav come from `@gjl/studio-chrome`, so a reader arriving
 * from a search result gets the same site the marketing pages are — and, more to
 * the point, a way out of the reference and into the product. These 968 pages first
 * shipped with no header and no footer at all, so every organic arrival dead-ended,
 * which defeats the reason for building them.
 */
import { Routes, Route } from 'react-router-dom'
import { ChromeShell, usePageEngagement, useReturnVisitTracker } from '@gjl/studio-chrome'

import { HymnalChromeProvider } from './components/chrome-config'
import HymnPage from './pages/HymnPage'
import TunePage from './pages/TunePage'
import PersonPage from './pages/PersonPage'
import MeterPage from './pages/MeterPage'
import SectionPage from './pages/SectionPage'
import { IndexPage } from './pages/IndexPages'
import HymnalHomePage from './pages/HymnalHomePage'
import PropersIndexPage from './pages/PropersIndexPage'
import PropersDayPage from './pages/PropersDayPage'
import PsalterIndexPage from './pages/PsalterIndexPage'
import PsalmPage from './pages/PsalmPage'
import { NotFound } from './components/NotFound'

/*
 * SPA PAGEVIEWS ARE NOT WIRED HERE. A local TrackPageviews used to live at this
 * spot, and `mountClient` (@gjl/site-kit) ALSO renders its own <PageviewTracker/>
 * — both keyed on [pathname, search], both firing. Every hymnal page_view since
 * launch was counted twice, which silently doubled the one number this project
 * exists to produce. Removed 2026-09-04; the harness's tracker is the single
 * emitter. Do not add another: if pageviews stop, check `initGA4` in main.tsx,
 * not this file.
 */

export default function App() {
  // The same GA4 engagement events the marketing site fires, from the same
  // source. Both builds report into property 527738893, so these have to BE the
  // same code — otherwise the funnel from a hymn page through to Canticle stops
  // being comparable exactly at the boundary it crosses, which is the one
  // measurement this project exists to make.
  usePageEngagement()
  useReturnVisitTracker()

  // NOT `useLenis()`, and that is the one deliberate break from studio's layout.
  // Measured: Lenis + GSAP cost 51.4KB gzip of a 68.4KB total increase — 75% of
  // the price of the whole chrome, to smooth scrolling. These 968 pages exist to
  // rank and to be read once by someone who arrived from a search result, so
  // paying three quarters of the budget for scroll feel is the wrong trade here
  // in a way it is not on a marketing page a prospect lingers over. Restoring it
  // is a two-line change if that judgement ever looks wrong.

  return (
    <HymnalChromeProvider>
      <ChromeShell>
        <Routes>
          <Route path="/hymnal/hymnal1982" element={<HymnalHomePage />} />
          <Route path="/hymnal/hymnal1982/hymn/:number" element={<HymnPage />} />
          <Route path="/hymnal/hymnal1982/tune/:slug" element={<TunePage />} />
          <Route path="/hymnal/hymnal1982/person/:slug" element={<PersonPage />} />
          <Route path="/hymnal/hymnal1982/meter/:slug" element={<MeterPage />} />
          <Route path="/hymnal/hymnal1982/section/:slug" element={<SectionPage />} />
          <Route path="/hymnal/hymnal1982/hymns" element={<IndexPage kind="hymns" />} />
          <Route path="/hymnal/hymnal1982/tunes" element={<IndexPage kind="tunes" />} />
          <Route path="/hymnal/hymnal1982/people" element={<IndexPage kind="people" />} />
          <Route path="/hymnal/hymnal1982/meters" element={<IndexPage kind="meters" />} />
          {/* Propers are a second reference under the same prefix, not part of the
              1982 hymnal: they come from a different book and are keyed by
              liturgical day rather than hymn number. Hence /hymnal/propers/* and
              not /hymnal/hymnal1982/propers/*. */}
          <Route path="/hymnal/propers" element={<PropersIndexPage />} />
          <Route path="/hymnal/propers/:slug" element={<PropersDayPage />} />
          {/* The psalter is a THIRD reference under the prefix, and the only one
              whose FULL TEXT we may publish: the 1979 BCP is public domain where
              the 1982's texts are Church Publishing's. Keyed by psalm number, so
              it sits beside the hymnal rather than inside it — same reasoning as
              propers above. */}
          <Route path="/hymnal/psalter" element={<PsalterIndexPage />} />
          <Route path="/hymnal/psalter/:number" element={<PsalmPage />} />
          <Route
            path="*"
            element={
              <NotFound
                title="Not found"
                detail="That page is not part of the hymnal reference."
                backTo="/hymnal/hymnal1982"
                backLabel="Browse The Hymnal 1982"
              />
            }
          />
        </Routes>
      </ChromeShell>
    </HymnalChromeProvider>
  )
}
